<script setup>
import { ref, computed, inject, onMounted, onBeforeUnmount } from "vue";
import ProfileService from "../../services/profileService.js";

const props = defineProps({
  userEmail: { type: String, default: "" },
});
const emit = defineEmits(["close"]);

const $auth0 = inject("$auth0");
const profileService = new ProfileService();

function close() { emit("close"); }
function onEsc(e) { if (e.key === "Escape") close(); }
onMounted(() => document.addEventListener("keydown", onEsc));
onBeforeUnmount(() => document.removeEventListener("keydown", onEsc));

const confirmEmail = ref("");
const showError = ref(false);
const isDeleting = ref(false);

const canDelete = computed(() =>
  confirmEmail.value.trim() && props.userEmail && confirmEmail.value.trim().toLowerCase() === props.userEmail.trim().toLowerCase()
);

async function onDelete() {
  if (!canDelete.value) {
    showError.value = true;
    return;
  }
  isDeleting.value = true;
  try {
    await profileService.deleteAccount();
    await $auth0.signOut();
  } catch (error) {
    console.error("Failed to delete account", error);
    isDeleting.value = false;
  }
}
</script>

<template>
  <div class="fixed inset-0 bg-black/20 dark:bg-black/60 z-[500] flex items-center justify-center p-4" @click.self="close">
    <div class="relative bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-[480px] flex flex-col overflow-hidden z-[520]">

      <button
        class="absolute top-4 right-4 flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-slate-600 dark:hover:text-slate-200 cursor-pointer transition-colors"
        @click="close"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
        </svg>
      </button>

      <!-- Icon + title -->
      <div class="flex flex-col items-center px-6 pt-8 pb-2">
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
          <path
            d="M8.47301 33.206L20.0669 12.2076C21.6924 9.26351 26.3202 9.26428 27.9445 12.209L39.5277 33.2073C40.9952 35.8676 38.8651 39 35.5885 39H12.4115C9.13418 39 7.00412 35.8664 8.47301 33.206Z"
            class="fill-red-100 dark:fill-red-950/60"
          />
          <path
            d="M23.9985 18.4962V27.4974M20.0669 12.2076L8.47301 33.206C7.00412 35.8664 9.13418 39 12.4115 39H35.5885C38.8651 39 40.9952 35.8676 39.5277 33.2073L27.9445 12.209C26.3202 9.26428 21.6924 9.26351 20.0669 12.2076Z"
            class="stroke-red-500 dark:stroke-red-400" stroke-width="2" stroke-linecap="round"
          />
          <circle cx="24" cy="32.5" r="1.5" class="fill-red-500 dark:fill-red-400" />
        </svg>
        <h2 class="text-lg font-bold text-slate-800 dark:text-slate-100 mt-3 mb-0 text-center">Delete Account - Are you sure?</h2>
      </div>

      <!-- Details -->
      <div class="mx-6 mt-4 bg-slate-50 dark:bg-slate-800 rounded-xl p-4">
        <div class="flex items-start gap-2 mb-3">
          <svg class="shrink-0 mt-0.5 text-green-500 dark:text-green-400" width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M20 6L9 17l-5-5" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          <div>
            <p class="text-sm font-bold text-slate-800 dark:text-slate-100 m-0 mb-1">What gets deleted</p>
            <ul class="m-0 pl-4 text-sm text-slate-500 dark:text-slate-400 list-disc space-y-0.5">
              <li>Your projects and the documents in them</li>
              <li>Annotations in your projects</li>
              <li>Comments and replies in your projects</li>
            </ul>
          </div>
        </div>
        <div class="flex items-start gap-2">
          <svg class="shrink-0 mt-0.5 text-red-500 dark:text-red-400" width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" />
          </svg>
          <div>
            <p class="text-sm font-bold text-slate-800 dark:text-slate-100 m-0 mb-1">What doesn't get deleted</p>
            <ul class="m-0 pl-4 text-sm text-slate-500 dark:text-slate-400 list-disc space-y-0.5">
              <li>Your contributions in projects owned by others</li>
            </ul>
          </div>
        </div>
        <div class="h-px bg-slate-200 dark:bg-slate-700 my-3" />
        <p class="text-sm text-slate-500 dark:text-slate-400 m-0 text-center">
          Still have questions? Contact us at
          <a class="text-blue-500 dark:text-blue-400 hover:underline" href="mailto:help@folia.com">help@folia.com</a>
        </p>
      </div>

      <!-- Confirm input -->
      <div class="px-6 pt-4 pb-2">
        <label class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">Please enter your email address to confirm</label>
        <input
          v-model="confirmEmail"
          type="email"
          :placeholder="userEmail"
          :class="showError ? 'border-red-400 ring-1 ring-red-300 dark:border-red-500 dark:ring-red-900' : 'border-slate-200 dark:border-slate-600'"
          class="w-full bg-slate-50 dark:bg-slate-800 border rounded-xl px-4 py-3 text-sm text-slate-800 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 outline-none focus:ring-2 focus:ring-red-200 dark:focus:ring-red-900 transition-shadow"
          @keydown.enter="onDelete"
        />
        <p v-if="showError" class="text-xs text-red-600 dark:text-red-400 mt-2 mb-0">Type {{ userEmail }} to continue</p>
      </div>

      <!-- Actions -->
      <div class="flex items-center justify-center gap-3 px-6 py-5 mt-2 bg-slate-50 dark:bg-slate-800/60 border-t border-slate-200 dark:border-slate-700">
        <button
          class="px-6 py-2.5 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-sm font-semibold hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors cursor-pointer"
          @click="close"
        >
          Cancel
        </button>
        <button
          :disabled="isDeleting"
          class="px-6 py-2.5 rounded-xl bg-red-600 text-white text-sm font-semibold hover:bg-red-500 transition-colors cursor-pointer disabled:opacity-50"
          @click="onDelete"
        >
          {{ isDeleting ? "Deleting…" : "Delete account" }}
        </button>
      </div>
    </div>
  </div>
</template>
